import { authorizedFetch, ErrorResponse } from './api-client';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// --- Types ---

export type ClaimStatus =
  | 'SOLICITADO'
  | 'AUTORIZADO'
  | 'NEGADO'
  | 'CANCELADO'
  | 'ASSINADO'
  | 'EM_ANALISE'
  | 'GLOSADO'
  | 'GLOSADO_PERDA'
  | 'GLOSA_RECURSO'
  | 'EM_RECEBIMENTO'
  | 'PAGO_CONVENIO'
  | 'NAO_PAGO_CONVENIO'
  | 'PARCIAL';

export const CLAIM_STATUS_LABELS: Record<ClaimStatus, string> = {
  SOLICITADO: 'Solicitado',
  AUTORIZADO: 'Autorizado',
  NEGADO: 'Negado',
  CANCELADO: 'Cancelado',
  ASSINADO: 'Assinado',
  EM_ANALISE: 'Em Análise',
  GLOSADO: 'Glosado',
  GLOSADO_PERDA: 'Glosa — Perda',
  GLOSA_RECURSO: 'Glosa — Recurso',
  EM_RECEBIMENTO: 'Em Recebimento',
  PAGO_CONVENIO: 'Pago pelo Convênio',
  NAO_PAGO_CONVENIO: 'Não Pago',
  PARCIAL: 'Parcial',
};

export interface InsuranceClaimResponse {
  id: string;
  claim_status: ClaimStatus;
  claim_number?: string;
  gto_file_url?: string;
  created_at: string;
  updated_at?: string;
}

export interface InsuranceAuthorizationRequestResponse {
  id: string;
  appointment_id: string;
  clinic_id: string;
  dental_insurance_id: string;
  description?: string;
  status: number;
  created_at: string;
  updated_at?: string;
  claims: InsuranceClaimResponse[];
}

export interface InsuranceAuthorizationRequestDetail extends InsuranceAuthorizationRequestResponse {
  appointment_date?: string;
  appointment_time?: string;
  patient_name?: string;
  dentist_name?: string;
  dental_insurance_name?: string;
  linked_guides_count?: number;
}

// --- API calls ---

export async function createAuthorizationRequest(data: {
  appointment_id: string;
  clinic_id: string;
  dental_insurance_id: string;
  description: string;
}): Promise<InsuranceAuthorizationRequestResponse> {
  const { clinic_id, ...body } = data;
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/insurance-authorizations/requests?clinic_id=${clinic_id}`,
    { method: 'POST', body: JSON.stringify(body) }
  );

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao criar solicitação de autorização');
  }

  return response.json();
}

export interface AuthorizationsFilters {
  patientName?: string;
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string;   // YYYY-MM-DD
  claimStatus?: ClaimStatus;
}

export async function fetchAuthorizationsByClinic(
  clinicId: string,
  page = 1,
  pageSize = 20,
  filters: AuthorizationsFilters = {}
): Promise<{ items: InsuranceAuthorizationRequestDetail[]; total: number; page: number; page_size: number }> {
  const params = new URLSearchParams({
    clinic_id: clinicId,
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  if (filters.patientName?.trim()) params.append('patient_name', filters.patientName.trim());
  if (filters.dateFrom) params.append('date_from', filters.dateFrom);
  if (filters.dateTo) params.append('date_to', filters.dateTo);
  if (filters.claimStatus) params.append('claim_status', filters.claimStatus);

  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/insurance-authorizations/requests?${params}`
  );

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao carregar autorizações');
  }

  return response.json();
}

export async function fetchAuthorizationDetail(
  requestId: string
): Promise<InsuranceAuthorizationRequestDetail> {
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/insurance-authorizations/requests/${requestId}`
  );

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao carregar detalhe da autorização');
  }

  return response.json();
}

export async function updateClaimStatus(
  claimId: string,
  newStatus: ClaimStatus,
  notes?: string
): Promise<void> {
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/insurance-authorizations/claims/${claimId}/status`,
    {
      method: 'POST',
      body: JSON.stringify({ to_status: newStatus, notes }),
    }
  );

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao atualizar status');
  }
}

export async function fetchClaimsByStatus(
  claimStatus: ClaimStatus,
  clinicId?: string
): Promise<{ items: InsuranceAuthorizationRequestDetail[]; total: number }> {
  const params = new URLSearchParams({ claim_status: claimStatus });
  if (clinicId) params.append('clinic_id', clinicId);

  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/insurance-authorizations/claims?${params}`
  );

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao carregar solicitações');
  }

  return response.json();
}

// ── Faturamento — guias a partir de ASSINADO ──────────────────────────────────

export interface BillingClaimRow {
  id: string;
  insurance_authorization_request_id: string;
  claim_number?: string;
  claim_status: ClaimStatus;
  gto_file_url?: string;
  created_at: string;
  patient_name?: string;
  dental_insurance_name?: string;
  appointment_date?: string;
  appointment_time?: string;
  procedures_count: number;
  documents_count: number;
}

export interface BillingFilters {
  patientName?: string;
  dateFrom?: string;
  dateTo?: string;
  claimStatus?: ClaimStatus;
}

export async function fetchBillingClaims(
  clinicId: string,
  page = 1,
  pageSize = 20,
  filters: BillingFilters = {}
): Promise<{ items: BillingClaimRow[]; total: number; page: number; page_size: number }> {
  const params = new URLSearchParams({
    clinic_id: clinicId,
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  if (filters.patientName?.trim()) params.append('patient_name', filters.patientName.trim());
  if (filters.dateFrom) params.append('date_from', filters.dateFrom);
  if (filters.dateTo) params.append('date_to', filters.dateTo);
  if (filters.claimStatus) params.append('claim_status', filters.claimStatus);

  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/insurance-authorizations/claims/billing?${params}`
  );

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao carregar faturamento');
  }

  return response.json();
}

// ── Documentos (guia e procedimento) ──────────────────────────────────────────

export interface DocumentResponse {
  id: string;
  document_type: string;
  file_url: string;
  original_filename?: string;
  mime_type?: string;
  size_bytes?: number;
  created_at?: string;
  insurance_claim_id?: string;
  claim_procedure_id?: string;
}

async function uploadMultipart(url: string, file: File, documentType: string): Promise<DocumentResponse> {
  const form = new FormData();
  form.append('file', file);
  form.append('document_type', documentType);
  const response = await authorizedFetch(url, { method: 'POST', body: form });
  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao enviar arquivo');
  }
  return response.json();
}

export async function uploadClaimDocument(claimId: string, file: File, documentType = 'attachment') {
  return uploadMultipart(
    `${API_BASE_URL}/api/v1/insurance-authorizations/claims/${claimId}/documents`,
    file,
    documentType,
  );
}

export async function listClaimDocuments(claimId: string): Promise<DocumentResponse[]> {
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/insurance-authorizations/claims/${claimId}/documents`
  );
  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao carregar documentos');
  }
  return response.json();
}

export async function deleteClaimDocument(docId: string): Promise<void> {
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/insurance-authorizations/claim-documents/${docId}`,
    { method: 'DELETE' }
  );
  if (!response.ok && response.status !== 204) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao excluir documento');
  }
}

export async function fetchClaimDocumentBlob(docId: string): Promise<Blob> {
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/insurance-authorizations/claim-documents/${docId}/download`
  );
  if (!response.ok) {
    throw new Error('Erro ao baixar documento');
  }
  return response.blob();
}

export async function uploadProcedureDocument(procedureId: string, file: File, documentType = 'attachment') {
  return uploadMultipart(
    `${API_BASE_URL}/api/v1/insurance-authorizations/procedures/${procedureId}/documents`,
    file,
    documentType,
  );
}

export async function listProcedureDocuments(procedureId: string): Promise<DocumentResponse[]> {
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/insurance-authorizations/procedures/${procedureId}/documents`
  );
  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao carregar documentos');
  }
  return response.json();
}

export async function deleteProcedureDocument(docId: string): Promise<void> {
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/insurance-authorizations/procedure-documents/${docId}`,
    { method: 'DELETE' }
  );
  if (!response.ok && response.status !== 204) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao excluir documento');
  }
}

export async function fetchProcedureDocumentBlob(docId: string): Promise<Blob> {
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/insurance-authorizations/procedure-documents/${docId}/download`
  );
  if (!response.ok) {
    throw new Error('Erro ao baixar documento');
  }
  return response.blob();
}

export function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
